/**
 * dealAnalyzer.js — Pure, deterministic calculation engine for the
 * Propertic Dubai Property Deal Analyzer.
 *
 * Principles (locked by PD spec V1):
 *  1. Zero external data. No fetches. No Propertic market prices. No AI.
 *  2. Zero opinion. We calculate. We do NOT tell the user if a deal is good.
 *  3. Single source of truth. UI reads from these functions and only these.
 *  4. All money is stored in AED internally. USD is a display-only concern.
 *
 * Every function is pure: same inputs → same outputs, no side effects.
 * Every function is null-safe: undefined / negative / NaN inputs coerce
 * to safe zero or clamped ranges so the UI never renders NaN.
 */

// ---------- helpers ----------

/** Coerce anything into a non-negative finite number (0 if invalid). */
export const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : 0;
};

/** Coerce anything into any finite number (can be negative). */
export const signedNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** Clamp x into [lo, hi]. */
export const clamp = (x, lo, hi) => Math.min(hi, Math.max(lo, x));

/** Safe division: returns 0 if denominator is 0 (never NaN / Infinity). */
export const safeDiv = (a, b) => (b === 0 ? 0 : a / b);

/**
 * Standard mortgage EMI (monthly amortized payment).
 * Handles the rate=0 edge case (interest-free tenor → linear amortization).
 */
export function computeMonthlyPayment(principal, annualRatePct, termYears) {
  const p = num(principal);
  const r = num(annualRatePct) / 100 / 12;
  const n = Math.round(num(termYears) * 12);
  if (n === 0) return 0;
  if (r === 0) return p / n;
  return (p * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
}

/**
 * Outstanding balance after `monthsElapsed` months of amortization.
 * Uses closed-form so we do not loop for O(N) each call.
 */
export function outstandingBalanceAtMonth(principal, annualRatePct, termYears, monthsElapsed) {
  const p = num(principal);
  const r = num(annualRatePct) / 100 / 12;
  const n = Math.round(num(termYears) * 12);
  const k = clamp(Math.round(monthsElapsed), 0, n);
  if (n === 0 || p === 0) return 0;
  if (r === 0) return Math.max(0, p * (1 - k / n));
  const factor = Math.pow(1 + r, n);
  const paidFactor = Math.pow(1 + r, k);
  return Math.max(0, p * (factor - paidFactor) / (factor - 1));
}

// ---------- acquisition ----------

/**
 * Compute all acquisition-side costs, kept explicit and traceable.
 *
 * Inputs:
 *   purchasePrice           — AED
 *   dldFeePct               — default 4 (percent of purchase price)
 *   brokerFee               — AED, optional flat amount (0 if using pct)
 *   brokerFeePct            — percent of purchase price, optional (0 if using flat)
 *                             If both supplied, they ADD (UI enforces one path
 *                             via a single-input toggle).
 *   otherPurchaseCosts      — AED, catch-all (developer admin charges, NOC,
 *                             valuation, insurance one-off, etc.)
 */
export function computeAcquisition({
  purchasePrice,
  dldFeePct = 4,
  brokerFee = 0,
  brokerFeePct = 0,
  otherPurchaseCosts = 0,
} = {}) {
  const price = num(purchasePrice);
  const dldFee = price * (num(dldFeePct) / 100);
  const brokerFeeCalc = num(brokerFee) + price * (num(brokerFeePct) / 100);
  const otherCosts = num(otherPurchaseCosts);
  const totalAcquisitionCosts = dldFee + brokerFeeCalc + otherCosts;
  const totalAcquisitionCost = price + totalAcquisitionCosts;
  return {
    purchasePrice: price,
    dldFee,
    brokerFee: brokerFeeCalc,
    otherPurchaseCosts: otherCosts,
    totalAcquisitionCosts,        // acquisition FEES ONLY (excl price)
    totalAcquisitionCost,         // price + fees (money out the door at close)
  };
}

// ---------- financing ----------

/**
 * Compute the financing structure. Two supported modes:
 *   mode === "cash"     — no mortgage, initial cash = price + acquisition fees
 *   mode === "mortgage" — down payment + mortgage
 *
 * downPayment is AED (flat). downPaymentPct is a percent-of-price alternative.
 * If both supplied, downPaymentPct wins (matches the UI radio toggle).
 */
export function computeFinancing({
  mode = "cash",
  purchasePrice,
  totalAcquisitionCosts = 0,
  downPayment = 0,
  downPaymentPct = 0,
  interestRatePct = 0,
  termYears = 0,
  mortgageCosts = 0,
} = {}) {
  const price = num(purchasePrice);
  const acqFees = num(totalAcquisitionCosts);

  if (mode === "cash") {
    return {
      mode: "cash",
      downPayment: price,
      mortgageAmount: 0,
      monthlyPayment: 0,
      annualDebtService: 0,
      mortgageCosts: 0,
      // In cash mode the buyer funds the full price + acquisition fees at close.
      initialCashRequired: price + acqFees,
    };
  }

  const dpFromPct = num(downPaymentPct) > 0 ? price * (num(downPaymentPct) / 100) : 0;
  const dp = clamp(dpFromPct || num(downPayment), 0, price);
  const loan = Math.max(0, price - dp);
  const monthlyPayment = computeMonthlyPayment(loan, interestRatePct, termYears);
  const annualDebtService = monthlyPayment * 12;
  const initialCashRequired = dp + acqFees + num(mortgageCosts);

  return {
    mode: "mortgage",
    downPayment: dp,
    mortgageAmount: loan,
    monthlyPayment,
    annualDebtService,
    mortgageCosts: num(mortgageCosts),
    initialCashRequired,
  };
}

// ---------- returns ----------

/**
 * Compute the whole return picture for a single steady-state year.
 * Vacancy is applied to gross rent before opex to get effective NOI.
 */
export function computeReturns({
  purchasePrice,
  totalAcquisitionCost,
  annualRent,
  annualOperatingCosts,
  annualDebtService = 0,
  initialCashInvested,
  vacancyPct = 0,
} = {}) {
  const rent = num(annualRent);
  const opex = num(annualOperatingCosts);
  const debt = num(annualDebtService);
  const cash = num(initialCashInvested);
  const price = num(purchasePrice);
  const acqTotal = num(totalAcquisitionCost);
  const vac = clamp(num(vacancyPct), 0, 100);

  const effectiveAnnualRent = rent * (1 - vac / 100);
  const netOperatingIncome = effectiveAnnualRent - opex;
  const annualCashFlow = netOperatingIncome - debt;

  // Break-even rent: minimum GROSS annual rent that produces zero cash flow
  // after opex, debt service and vacancy loss.
  //   (rent * (1 - vac) - opex - debt) = 0
  //   rent = (opex + debt) / (1 - vac)
  const breakEvenAnnualRent = vac >= 100 ? Infinity : (opex + debt) / (1 - vac / 100);

  return {
    grossYieldPct: safeDiv(rent, price) * 100,
    netYieldPct: safeDiv(netOperatingIncome, acqTotal) * 100,
    effectiveAnnualRent,
    netOperatingIncome,
    annualCashFlow,
    cashOnCashPct: safeDiv(annualCashFlow, cash) * 100,
    breakEvenAnnualRent,
  };
}

// ---------- required rent (backward math) ----------

/**
 * Given a TARGET net yield, work backwards to the annual (and monthly) rent
 * the property must earn. Spec: net yield = NOI / totalAcquisitionCost.
 * NOI = effectiveRent - opex, so:
 *   effectiveRent = target * acqTotal + opex
 *   annualRent    = effectiveRent / (1 - vac)
 */
export function computeRequiredRent({
  targetNetYieldPct,
  totalAcquisitionCost,
  annualOperatingCosts = 0,
  vacancyPct = 0,
  enteredAnnualRent = 0,
} = {}) {
  const target = num(targetNetYieldPct) / 100;
  const acq = num(totalAcquisitionCost);
  const opex = num(annualOperatingCosts);
  const vacRaw = num(vacancyPct);
  const vac = clamp(vacRaw, 0, 100);

  const effectiveRent = target * acq + opex;
  const entered = num(enteredAnnualRent);
  // At 100% vacancy no rent level can produce income: report "not achievable"
  // instead of a finite number from a substituted vacancy.
  if (vac >= 100) {
    return { requiredAnnualRent: null, requiredMonthlyRent: null, enteredAnnualRent: entered,
      differenceAnnualRent: null, notAchievable: true };
  }
  const annualRent = effectiveRent / (1 - vac / 100);
  const monthlyRent = annualRent / 12;
  const difference = entered > 0 ? annualRent - entered : null;

  return {
    requiredAnnualRent: annualRent,
    requiredMonthlyRent: monthlyRent,
    enteredAnnualRent: entered,
    differenceAnnualRent: difference,   // + means user rent is short; − means user beats target
    notAchievable: false,
  };
}

// ---------- N-year scenario projection ----------

/**
 * Year-by-year mathematical scenario. Explicitly framed as "SCENARIO", never
 * "expected" or "forecast". Every growth assumption is user-supplied.
 *
 * Compounding conventions:
 *   scenarioPropertyValue(y) = purchasePrice * (1 + appreciation)^y
 *   annualRent(y)            = annualRent    * (1 + rentGrowth)^(y-1)   ← year 1 = user input
 *   annualOpex(y)            = opex          * (1 + expenseGrowth)^(y-1)
 *   effectiveRent(y)         = annualRent(y) * (1 - vacancy)
 *   noi(y)                   = effectiveRent(y) - annualOpex(y)
 *   cashFlow(y)              = noi(y) - annualDebtService (debt service is fixed if fixed-rate mortgage)
 *   remainingMortgage(y)     = closed-form outstanding balance at 12*y months
 *   equity(y)                = scenarioPropertyValue(y) - remainingMortgage(y)
 */
export function projectYearByYear({
  purchasePrice,
  totalAcquisitionCost,
  annualRent,
  annualOperatingCosts,
  mortgageAmount = 0,
  monthlyPayment = 0,
  interestRatePct = 0,
  termYears = 0,
  initialCashInvested = 0,
  appreciationPct = 0,
  rentGrowthPct = 0,
  expenseGrowthPct = 0,
  vacancyPct = 0,
  holdingYears = 5,
} = {}) {
  const price = num(purchasePrice);
  const acqTotal = num(totalAcquisitionCost);
  const rent0 = num(annualRent);
  const opex0 = num(annualOperatingCosts);
  const loan = num(mortgageAmount);
  const pmt = num(monthlyPayment);
  const annualDebt = pmt * 12;
  const cash = num(initialCashInvested);
  const apprec = signedNum(appreciationPct) / 100;
  const rentG = signedNum(rentGrowthPct) / 100;
  const expG = signedNum(expenseGrowthPct) / 100;
  const vac = clamp(num(vacancyPct), 0, 100);
  const years = clamp(Math.round(num(holdingYears)), 1, 30);

  const rows = [];
  let cumulativeRent = 0;
  let cumulativeExpenses = 0;
  let cumulativeCashFlow = 0;

  for (let y = 1; y <= years; y += 1) {
    const scenarioPropertyValue = price * Math.pow(1 + apprec, y);
    const yearRent = rent0 * Math.pow(1 + rentG, y - 1);
    const yearOpex = opex0 * Math.pow(1 + expG, y - 1);
    const effectiveRent = yearRent * (1 - vac / 100);
    const noi = effectiveRent - yearOpex;
    // Debt service stops at loan maturity (pro-rated for a fractional final year).
    const termMonths = Math.round(num(termYears) * 12);
    const monthsPaid = clamp(termMonths - (y - 1) * 12, 0, 12);
    const yearDebt = loan > 0 ? pmt * monthsPaid : annualDebt;
    const cashFlow = noi - yearDebt;
    cumulativeRent += yearRent;
    cumulativeExpenses += yearOpex + yearDebt;
    cumulativeCashFlow += cashFlow;
    const remainingMortgage = outstandingBalanceAtMonth(loan, interestRatePct, termYears, y * 12);
    const equity = scenarioPropertyValue - remainingMortgage;

    rows.push({
      year: y,
      scenarioPropertyValue,
      annualRent: yearRent,
      annualOperatingCosts: yearOpex,
      effectiveAnnualRent: effectiveRent,
      netOperatingIncome: noi,
      annualDebtService: yearDebt,
      annualCashFlow: cashFlow,
      cumulativeRent,
      cumulativeExpenses,
      cumulativeCashFlow,
      remainingMortgage,
      equity,
      totalCashInvested: cash,      // buyer never puts more cash in after close in this MVP
      // Simple total-return-on-cash: (equity - initialCash + cumulativeCashFlow) / initialCash
      // Displayed as SCENARIO figure, never labelled "expected".
      scenarioReturnOnCashPct: safeDiv(equity - cash + cumulativeCashFlow, cash) * 100,
    });
  }
  return rows;
}

// ---------- cash vs mortgage side-by-side ----------

/**
 * Convenience wrapper: run the engine twice (cash mode and mortgage mode) and
 * return both economics blocks. UI reads from this to build the comparison
 * table. NEVER labels either scenario as "better" — that is the user's call.
 */
export function compareCashVsMortgage(baseInputs = {}) {
  const acq = computeAcquisition(baseInputs);

  // Cash mode
  const cashFin = computeFinancing({
    ...baseInputs,
    mode: "cash",
    purchasePrice: acq.purchasePrice,
    totalAcquisitionCosts: acq.totalAcquisitionCosts,
  });
  const cashRet = computeReturns({
    purchasePrice: acq.purchasePrice,
    totalAcquisitionCost: acq.totalAcquisitionCost,
    annualRent: baseInputs.annualRent,
    annualOperatingCosts: baseInputs.annualOperatingCosts,
    annualDebtService: 0,
    initialCashInvested: cashFin.initialCashRequired,
    vacancyPct: baseInputs.vacancyPct,
  });

  // Mortgage mode
  const mortFin = computeFinancing({
    ...baseInputs,
    mode: "mortgage",
    purchasePrice: acq.purchasePrice,
    totalAcquisitionCosts: acq.totalAcquisitionCosts,
  });
  const mortRet = computeReturns({
    purchasePrice: acq.purchasePrice,
    totalAcquisitionCost: acq.totalAcquisitionCost,
    annualRent: baseInputs.annualRent,
    annualOperatingCosts: baseInputs.annualOperatingCosts,
    annualDebtService: mortFin.annualDebtService,
    initialCashInvested: mortFin.initialCashRequired,
    vacancyPct: baseInputs.vacancyPct,
  });

  // Equity after N years for both scenarios (default 5)
  const horizon = baseInputs.holdingYears || 5;
  const cashProjection = projectYearByYear({
    purchasePrice: acq.purchasePrice,
    totalAcquisitionCost: acq.totalAcquisitionCost,
    annualRent: baseInputs.annualRent,
    annualOperatingCosts: baseInputs.annualOperatingCosts,
    mortgageAmount: 0,
    monthlyPayment: 0,
    interestRatePct: 0,
    termYears: 0,
    initialCashInvested: cashFin.initialCashRequired,
    appreciationPct: baseInputs.appreciationPct,
    rentGrowthPct: baseInputs.rentGrowthPct,
    expenseGrowthPct: baseInputs.expenseGrowthPct,
    vacancyPct: baseInputs.vacancyPct,
    holdingYears: horizon,
  });
  const mortProjection = projectYearByYear({
    purchasePrice: acq.purchasePrice,
    totalAcquisitionCost: acq.totalAcquisitionCost,
    annualRent: baseInputs.annualRent,
    annualOperatingCosts: baseInputs.annualOperatingCosts,
    mortgageAmount: mortFin.mortgageAmount,
    monthlyPayment: mortFin.monthlyPayment,
    interestRatePct: baseInputs.interestRatePct,
    termYears: baseInputs.termYears,
    initialCashInvested: mortFin.initialCashRequired,
    appreciationPct: baseInputs.appreciationPct,
    rentGrowthPct: baseInputs.rentGrowthPct,
    expenseGrowthPct: baseInputs.expenseGrowthPct,
    vacancyPct: baseInputs.vacancyPct,
    holdingYears: horizon,
  });

  return {
    horizonYears: horizon,
    cash: {
      financing: cashFin,
      returns: cashRet,
      equityAtHorizon: cashProjection[cashProjection.length - 1]?.equity || 0,
      cumulativeCashFlowAtHorizon: cashProjection[cashProjection.length - 1]?.cumulativeCashFlow || 0,
    },
    mortgage: {
      financing: mortFin,
      returns: mortRet,
      equityAtHorizon: mortProjection[mortProjection.length - 1]?.equity || 0,
      cumulativeCashFlowAtHorizon: mortProjection[mortProjection.length - 1]?.cumulativeCashFlow || 0,
    },
  };
}

// ---------- one-shot: run the whole engine ----------

/**
 * Top-level convenience runner used by the UI. Takes ONE flat inputs object
 * and returns EVERY derived block in one deterministic object. UI never
 * re-implements formulas — it just reads from this result.
 */
export function analyzeDeal(inputs = {}) {
  const acquisition = computeAcquisition(inputs);
  const financing = computeFinancing({
    ...inputs,
    purchasePrice: acquisition.purchasePrice,
    totalAcquisitionCosts: acquisition.totalAcquisitionCosts,
  });
  const initialCashInvested = financing.initialCashRequired;
  const returns = computeReturns({
    purchasePrice: acquisition.purchasePrice,
    totalAcquisitionCost: acquisition.totalAcquisitionCost,
    annualRent: inputs.annualRent,
    annualOperatingCosts: inputs.annualOperatingCosts,
    annualDebtService: financing.annualDebtService,
    initialCashInvested,
    vacancyPct: inputs.vacancyPct,
  });
  const requiredRent = computeRequiredRent({
    targetNetYieldPct: inputs.targetNetYieldPct,
    totalAcquisitionCost: acquisition.totalAcquisitionCost,
    annualOperatingCosts: inputs.annualOperatingCosts,
    vacancyPct: inputs.vacancyPct,
    enteredAnnualRent: inputs.annualRent,
  });
  const projection = projectYearByYear({
    purchasePrice: acquisition.purchasePrice,
    totalAcquisitionCost: acquisition.totalAcquisitionCost,
    annualRent: inputs.annualRent,
    annualOperatingCosts: inputs.annualOperatingCosts,
    mortgageAmount: financing.mortgageAmount,
    monthlyPayment: financing.monthlyPayment,
    interestRatePct: inputs.interestRatePct,
    termYears: inputs.termYears,
    initialCashInvested,
    appreciationPct: inputs.appreciationPct,
    rentGrowthPct: inputs.rentGrowthPct,
    expenseGrowthPct: inputs.expenseGrowthPct,
    vacancyPct: inputs.vacancyPct,
    holdingYears: inputs.holdingYears,
  });
  const comparison = compareCashVsMortgage(inputs);
  return { acquisition, financing, returns, requiredRent, projection, comparison };
}
